import Fuse from 'fuse.js'

const FETCH_URL = '/wp-json/excelcar/v1/cars-index'
const CACHE_KEY = 'excelcar_search_index'
const CACHE_TTL_MS = 10 * 60 * 1000 // aligné sur le transient serveur (10 min)
const MIN_CHARS = 2
const DEBOUNCE_MS = 200
const TOP_N = 3

const FUSE_OPTIONS = {
  keys: [
    {
      name: 'title',
      weight: 0.5
    },
    {
      name: 'brand',
      weight: 0.3
    },
    {
      name: 'color',
      weight: 0.1
    },
    {
      name: 'no_vo',
      weight: 0.1
    }
  ],
  threshold: 0.35,
  ignoreLocation: true,
  minMatchCharLength: 2
}

let fuseInstance = null
let indexData = null
let indexPromise = null
const queryCache = new Map()

// Détecte une requête numéro VO pure (numérique uniquement → recherche stricte
// au lieu du fuzzy, pour permettre à l'atelier de retrouver un véhicule précis)
const VO_REGEX = /^\d+$/

const loadIndex = async () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY)
    if (cached) {
      const {
        data, expires
      } = JSON.parse(cached)
      if (Date.now() < expires && Array.isArray(data)) {
        return data
      }
    }
  } catch (e) {
    // sessionStorage indisponible ou JSON corrompu : on refetch
  }

  const res = await fetch(FETCH_URL, {
    credentials: 'same-origin'
  })
  if (!res.ok) {
    throw new Error(`Search index fetch failed: ${res.status}`)
  }
  const data = await res.json()

  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        data,
        expires: Date.now() + CACHE_TTL_MS
      })
    )
  } catch (e) {
    // quota plein : tant pis, on fonctionne sans cache persistant
  }

  return data
}

const ensureFuse = async () => {
  if (fuseInstance) {
    return fuseInstance
  }
  if (!indexPromise) {
    indexPromise = loadIndex()
  }
  const data = await indexPromise
  indexData = data
  fuseInstance = new Fuse(data, FUSE_OPTIONS)
  return fuseInstance
}

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const stripBrand = (title, brand) => {
  if (!brand) {
    return title
  }
  const escaped = brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return title.replace(new RegExp(`^${escaped}\\s*`, 'i'), '')
}

const renderResults = (results, container) => {
  if (!results.length) {
    const contactUrl = container.dataset.contactUrl || ''
    const contactLink = contactUrl
      ? `<a href="${escapeHtml(contactUrl)}" class="text-reset text-decoration-underline">contactez un conseiller</a>`
      : 'contactez un conseiller'
    container.innerHTML =
      '<div class="search-livesearch-empty text-muted text-center small py-3">' +
      `Aucun véhicule ne correspond. Affinez votre recherche ou ${contactLink}.` +
      '</div>'
    container.classList.remove('d-none')
    return
  }

  container.innerHTML = results
    .map(({
      item
    }) => {
      const model = stripBrand(item.title, item.brand)
      // price contient du HTML formaté côté serveur (ex: "134 900 € <small>TTC</small>"),
      // on le laisse en raw — les autres champs (user input / URLs) sont escapés.
      const metaParts = []
      if (item.year) {
        metaParts.push(escapeHtml(item.year))
      }
      if (item.price) {
        metaParts.push(item.price)
      }
      if (item.color) {
        metaParts.push(escapeHtml(item.color))
      }
      const meta = metaParts.join(' · ')
      const thumb = `<img src="${escapeHtml(item.thumbnail)}" class="search-result-thumb" alt="" loading="lazy">`

      return (
        `<a href="${escapeHtml(item.url)}" class="search-result-item d-flex align-items-center gap-3 text-decoration-none">${
          thumb
        }<span class="flex-grow-1 text-start">${
          item.brand
            ? `<small class="d-block text-muted text-uppercase">${escapeHtml(item.brand)}</small>`
            : ''
        }<span class="search-result-model fw-medium text-body">${escapeHtml(model)}</span>${
          meta ? `<span class="d-block small text-muted">${meta}</span>` : ''
        }</span>` +
        '</a>'
      )
    })
    .join('')

  container.classList.remove('d-none')
}

const debounce = (fn, wait) => {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), wait)
  }
}

export const initSearchLivesearch = () => {
  const inputs = document.querySelectorAll('[data-livesearch-input]')
  if (!inputs.length) {
    return
  }

  inputs.forEach((input) => {
    const form = input.closest('form')
    const resultsContainer = form?.querySelector('[data-livesearch-results]')
    if (!resultsContainer) {
      return
    }

    const search = debounce(async (query) => {
      if (queryCache.has(query)) {
        renderResults(queryCache.get(query), resultsContainer)
        return
      }

      try {
        const fuse = await ensureFuse()
        let results
        if (VO_REGEX.test(query)) {
          // Recherche stricte par numéro VO (pas de fuzzy)
          results = indexData
            .filter((item) => item.no_vo === query)
            .slice(0, TOP_N)
            .map((item) => ({
              item
            }))
        } else {
          results = fuse.search(query, {
            limit: TOP_N
          })
        }
        queryCache.set(query, results)
        renderResults(results, resultsContainer)
      } catch (err) {
        // En cas d'échec (réseau, JSON, etc.) on cache simplement le dropdown.
        // L'utilisateur peut toujours submit le form pour fallback sur la page search standard.
        resultsContainer.classList.add('d-none')
      }
    }, DEBOUNCE_MS)

    // Préchauffe l'index dès le 1er focus pour que la 1re saisie soit fluide
    input.addEventListener(
      'focus',
      () => {
        ensureFuse().catch(() => {})
      },
      {
        once: true
      }
    )

    input.addEventListener('input', () => {
      const query = input.value.trim()
      if (query.length < MIN_CHARS) {
        resultsContainer.classList.add('d-none')
        resultsContainer.innerHTML = ''
        return
      }
      search(query)
    })
  })
}

if (document.readyState !== 'loading') {
  initSearchLivesearch()
} else {
  document.addEventListener('DOMContentLoaded', initSearchLivesearch)
}
